/**
 * PrRepo sobre Supabase. Misma lógica que DexiePrRepo.
 */

import type { PrRepo } from "../../repos/PrRepo";
import type { Pr } from "@app-types/entities";
import type { EstadoPr } from "@constants/estados";
import { puedeTransicionarPr } from "@/features/pr/prRules";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabasePrRepo extends SupabaseBaseRepo<Pr> implements PrRepo {
  constructor(tenantId: string) {
    super("pr", tenantId);
  }

  async list(filter?: Partial<Pr>): Promise<Pr[]> {
    const all = await this.listAll();
    if (filter?.estado) return all.filter((p) => p.estado === filter.estado);
    if (filter?.afeId) return all.filter((p) => p.afeId === filter.afeId);
    return all.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async listByAfe(afeId: string): Promise<Pr[]> {
    return (await this.listAll()).filter((p) => p.afeId === afeId);
  }

  async listByEstado(estado: EstadoPr): Promise<Pr[]> {
    return (await this.listAll()).filter((p) => p.estado === estado);
  }

  async get(id: string): Promise<Pr | null> {
    return this.getById(id);
  }

  async create(data: Omit<Pr, "id" | "createdAt" | "updatedAt" | "version">): Promise<string> {
    const now = new Date().toISOString();
    const nuevo: Pr = {
      ...data,
      id: crypto.randomUUID(),
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(nuevo);
    return nuevo.id;
  }

  async update(id: string, data: Partial<Pr>, expectedVersion: number): Promise<void> {
    const current = await this.get(id);
    if (!current) throw new Error(`PR ${id} not found`);
    const updated: Pr = {
      ...current,
      ...data,
      // Auditoria de creacion INMUTABLE: ver BaseRepo.update.
      createdBy: current.createdBy,
      createdAt: current.createdAt,
      version: current.version + 1,
      updatedAt: new Date().toISOString(),
    };
    await this.updateRaw(updated, expectedVersion);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }

  async changeState(id: string, nextState: EstadoPr, userEmail: string, observaciones?: string): Promise<void> {
    const pr = await this.get(id);
    if (!pr) throw new Error(`PR ${id} not found`);
    if (pr.estado === nextState) return;
    if (!puedeTransicionarPr(pr.estado, nextState)) {
      throw new Error(`Transicion ${pr.estado} -> ${nextState} no permitida`);
    }
    const entry = {
      fecha: new Date().toISOString(),
      estadoAnterior: pr.estado,
      estadoNuevo: nextState,
      usuario: userEmail || "system",
      ...(observaciones && observaciones.trim() ? { observaciones } : {}),
    };
    const historial = [...(pr.historial ?? []), entry];
    const partial: Partial<Pr> = { estado: nextState, historial };
    if (nextState === "CANCELADA" && observaciones && observaciones.trim()) {
      partial.motivoCancelacion = observaciones;
    }
    await this.update(id, partial, pr.version);
  }
}
